import React, { useRef, useState } from "react";
import api from "../lib/api";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

/**
 * Logo uploader. Accepts PNG/JPG/SVG/WebP up to 10MB and uploads via /api/upload,
 * then calls onUploaded(url) with the public URL (e.g. /api/files/abc.png) that
 * can be stored on the tenant branding doc.
 *
 * Props:
 *  - label: string
 *  - value: current url (preview shown)
 *  - onChange: fn(url) called both when user uploads and when URL is typed
 *  - testid: data-testid prefix
 *  - aspect: "square" | "wide" — controls preview box ratio
 */
export default function LogoUploader({ label, value, onChange, testid, aspect = "wide" }) {
  const { t } = useI18n();
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(t("brand.too_big")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(t("brand.uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar arquivo");
    } finally { setUploading(false); }
  };

  return (
    <div>
      <label className="sw-label">{label}</label>
      <div className="flex items-start gap-3">
        {/* Preview */}
        <div
          className={`shrink-0 border border-[#e2e8f0] rounded-md flex items-center justify-center bg-[#fafafa] ${
            aspect === "square" ? "w-20 h-20" : "w-32 h-20"
          }`}
        >
          {value ? (
            <img src={value} alt="" className="max-w-full max-h-full object-contain p-2" />
          ) : (
            <ImageIcon size={20} className="text-[#cbd5e1]" />
          )}
        </div>
        {/* Controls */}
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="sw-input text-[12px]"
            placeholder={t("brand.url_ph")}
            data-testid={testid ? `${testid}-url` : undefined}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="sw-btn-secondary !py-1.5 !px-3 !text-[12px]"
              data-testid={testid ? `${testid}-upload-btn` : undefined}
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? "Enviando..." : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="sw-btn-secondary !py-1.5 !px-3 !text-[12px]"
                data-testid={testid ? `${testid}-clear-btn` : undefined}
              >
                <X size={13} /> Remover
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
